import {skipToken} from '@reduxjs/toolkit/query';
import {Card, DataStatus, Flag} from 'platform/components';
import {Heading, HStack, Show, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {isNotNil, reject} from 'ramda';

import {useGetGridTotalRowsQuery} from '@dms/api/core';
import {
  FINANCING_ENDING_PREFLIGHT_BLOCK_REASON,
  type FinancingEndingPreflightResponseBody,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';

import type {Nullish} from 'shared/web';

import {FINANCING_ENDING_GRID_CODE} from '../../constants/FINANCING_ENDING_GRID_CODE';
import {
  FinancingEndingSkipSummary,
  type FinancingEndingRowLabels,
} from '../FinancingEndingSkipSummary/FinancingEndingSkipSummary';

export type FinancingEndingRecipientSummaryCardProps = {
  preflight: FinancingEndingPreflightResponseBody | Nullish;
  isLoading: boolean;
  rowLabels: FinancingEndingRowLabels[];
  dataQueryId: string | Nullish;
};

const isEligible = (row: FinancingEndingPreflightResponseBody['rows'][number]) => row.eligible;

export function FinancingEndingRecipientSummaryCard(
  props: FinancingEndingRecipientSummaryCardProps
) {
  const {data: totalRowCount} = useGetGridTotalRowsQuery(
    isNotNil(props.dataQueryId)
      ? {code: FINANCING_ENDING_GRID_CODE, dataQueryId: props.dataQueryId}
      : skipToken
  );

  return (
    <Card variant="inlineWhite" data-testid={testIds.businessCase.financingEnding('summaryCard')}>
      <DataStatus isLoading={props.isLoading} data={props.preflight}>
        {({data: preflight}) => {
          const skippedRows = reject(isEligible, preflight.rows);
          const selectionText = isNotNil(totalRowCount)
            ? i18n.t('entity.businessCase.financingEnding.summary.selectedOfTotal', {
                selected: preflight.selectedCount,
                total: totalRowCount,
              })
            : i18n.t('entity.businessCase.financingEnding.summary.selectedOnly', {
                count: preflight.selectedCount,
              });

          const heading = match(preflight)
            .with({blockReason: FINANCING_ENDING_PREFLIGHT_BLOCK_REASON.OVER_LIMIT}, () =>
              i18n.t('entity.businessCase.financingEnding.summary.overLimit', {
                cap: preflight.cap,
                plannedCount: preflight.plannedCount,
              })
            )
            .with({blockReason: FINANCING_ENDING_PREFLIGHT_BLOCK_REASON.NONE_ELIGIBLE}, () =>
              i18n.t('entity.businessCase.financingEnding.summary.noneEligible')
            )
            .otherwise(() =>
              i18n.t('entity.businessCase.financingEnding.summary.willReceive', {
                count: preflight.plannedCount,
              })
            );

          return (
            <VStack spacing={4}>
              <HStack spacing={4} justify="space-between" align="flex-start">
                <VStack spacing={1}>
                  <Heading size={4}>{heading}</Heading>
                  <Text size="small" color="tertiary">
                    {i18n.t('entity.businessCase.financingEnding.summary.statusLine', {
                      skipped: i18n.t('entity.businessCase.financingEnding.summary.skippedCount', {
                        count: preflight.skippedCount,
                      }),
                      selected: selectionText,
                    })}
                  </Text>
                </VStack>
                {match(preflight)
                  .when(
                    (current) => current.skippedCount > 0,
                    () => (
                      <Flag
                        colorScheme="orange"
                        isSubtle
                        label={i18n.t('entity.businessCase.financingEnding.summary.skippedCount', {
                          count: preflight.skippedCount,
                        })}
                        data-testid={testIds.businessCase.financingEnding(
                          'summaryCard-skippedFlag'
                        )}
                      />
                    )
                  )
                  .with({canSend: true}, () => (
                    <Flag
                      colorScheme="green"
                      isSubtle
                      label={i18n.t('general.labels.ready')}
                      data-testid={testIds.businessCase.financingEnding('summaryCard-readyFlag')}
                    />
                  ))
                  .otherwise(() => null)}
              </HStack>
              <Show when={skippedRows.length > 0}>
                <FinancingEndingSkipSummary skippedRows={skippedRows} rowLabels={props.rowLabels} />
              </Show>
            </VStack>
          );
        }}
      </DataStatus>
    </Card>
  );
}
